import { useState, useEffect, useRef } from 'react';
import { Settings, LogOut, Sun, Moon } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { useTheme } from '../../hooks/useTheme';
import { Avatar } from './Avatar';
import { LogoutModal } from '../auth/LogoutModal';

interface UserMenuProps {
  onClose: () => void;
}

export function UserMenu({ onClose }: UserMenuProps) {
  const { logout, user } = useAuth0();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Ne pas fermer le menu si la modale de déconnexion est ouverte
        if (!showLogoutModal) {
          onClose();
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, showLogoutModal]);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      // 1. Nettoyer les données locales et invalider la session backend
      await authService.logout();
    } catch (error) {
      console.error('Error during logout cleanup', error);
    }
    
    // 2. Déconnecter Auth0 avec redirection vers notre page de login
    // federated: true force la déconnexion du fournisseur d'identité (Google, etc.)
    logout({ 
      logoutParams: { 
        returnTo: import.meta.env.VITE_AUTH0_LOGOUT_URI || `${window.location.origin}/login`,
        // Forcer la déconnexion complète d'Auth0
        federated: false, // true déconnecte aussi du provider (Google, etc.)
      } 
    });
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  return (
    <div
      ref={menuRef}
      className="absolute right-0 mt-2 w-64 bg-background rounded-md shadow-lg border border-border"
    >
      {/* Profil utilisateur */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center gap-3">
          <Avatar user={user} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-foreground truncate">
              {user?.name || 'Utilisateur'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {user?.email}
            </div>
          </div>
        </div>
      </div>
      
      {/* Actions du menu */}
      <div className="py-1">
        <button
          onClick={() => {
            navigate('/settings');
            onClose();
          }}
          className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
        >
          <Settings className="h-4 w-4 mr-3" />
          Paramètres
        </button>
        
        <button
          onClick={() => {
            toggleTheme();
            // Ne pas fermer le menu lors du changement de thème
          }}
          className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
        >
          {theme === 'light' ? (
            <>
              <Moon className="h-4 w-4 mr-3" />
              Mode sombre
            </>
          ) : (
            <>
              <Sun className="h-4 w-4 mr-3" />
              Mode clair
            </>
          )}
        </button>
        
        <button
          onClick={handleLogoutClick}
          className="flex items-center w-full px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
        >
          <LogOut className="h-4 w-4 mr-3" />
          Déconnexion
        </button>
      </div>

      {/* Modal de confirmation de déconnexion */}
      <LogoutModal
        open={showLogoutModal}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
        isLoading={isLoggingOut}
      />
    </div>
  );
}